import { useEffect, useState, Fragment } from "react";
import api from "@/lib/api";
import { Users, ChevronDown, ChevronRight, TrendingUp, TrendingDown, BellRing } from "lucide-react";
import { toast } from "sonner";
import { EvoHeaders, EvoCells, EvoExportButtons } from "@/components/employees/EvolutionCells";
import { EmployeeEvolutionChart } from "@/components/employees/EmployeeEvolutionChart";

const fmtEur = (n) =>
  Number(n || 0).toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 2 });
const fmtNum2 = (n) => Number(n || 0).toLocaleString("es-ES", { maximumFractionDigits: 2 });

/** Tabla de empleados medidos SOLO en las moléculas a incentivar.
 *  Se usa en el plan de incentivos (por mes) y en Análisis por empleado. */
export function IncentivesEmployeeTable({ data, testid = "incentives-employees" }) {
  const [openEmp, setOpenEmp] = useState(null);
  const [onlyFalling, setOnlyFalling] = useState(false);
  const [sending, setSending] = useState(false);
  const d = data;
  if (!d || !d.employees?.length) return null;
  const evo = d.evolution_months || [];
  const nEvo = evo.length >= 2 ? evo.length + 1 : 0;
  const nFalling = d.employees.filter((e) => e.falling_2m).length;
  const rows = onlyFalling ? d.employees.filter((e) => e.falling_2m) : d.employees;
  const sendAlert = async () => {
    setSending(true);
    try {
      const { data: r } = await api.post(`/incentives/month/${d.month.id}/falling-alert`);
      if (r.sent) toast.success(`Aviso enviado a ${r.to}`); else toast.info("Nadie baja 2 meses seguidos");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo enviar el aviso");
    } finally { setSending(false); }
  };

  return (
    <>
    {d.month?.id && (
      <div className="flex flex-wrap items-center gap-3 mb-2 text-xs text-slate-500">
        {nEvo > 0 && <span>Evolución del % genérico desde el mes base hasta {d.month.label}.</span>}
        {evo.length >= 3 && (
          <>
            <label className={`inline-flex items-center gap-1.5 font-semibold cursor-pointer ${nFalling ? "text-rose-700" : "text-slate-500"}`}>
              <input type="checkbox" checked={onlyFalling} onChange={(ev) => setOnlyFalling(ev.target.checked)} data-testid={`${testid}-only-falling`} />
              Ver solo los que bajan 2 meses seguidos ({nFalling})
            </label>
            <button type="button" onClick={sendAlert} disabled={!nFalling || sending} data-testid={`${testid}-send-falling-alert`}
              className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-rose-600 hover:bg-rose-700 rounded-md px-2.5 py-1 disabled:opacity-40">
              <BellRing className="h-3.5 w-3.5" /> Enviarme el aviso por email
            </button>
          </>
        )}
        <span className="ml-auto"><EvoExportButtons url={`/incentives/month/${d.month.id}/employees`} filename="incentivos_evolucion_por_empleado" testid={`${testid}-export`} /></span>
      </div>
    )}
    <div className="border border-slate-200 rounded-lg overflow-hidden bg-white overflow-x-auto">
      <table className="w-full text-sm" data-testid={`${testid}-table`}>
        <thead className="bg-slate-900 text-white text-[10px] uppercase tracking-wider">
          <tr>
            <th className="text-left px-3 py-2">#</th>
            <th className="text-left px-3 py-2">Empleado</th>
            <th className="text-right px-3 py-2">% genérico</th>
            <th className="text-right px-3 py-2">vs media</th>
            <th className="text-right px-3 py-2">% mes anterior</th>
            <th className="text-right px-3 py-2">Δ mes</th>
            <EvoHeaders months={evo} />
            <th className="text-right px-3 py-2">Uds (gen/marca)</th>
            <th className="text-right px-3 py-2">€ marca por convertir</th>
            <th className="text-right px-3 py-2">Moléculas</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((e, i) => {
            const isOpen = openEmp === e.id;
            const dm = e.delta_vs_prev_pp;
            const red = e.below_avg || e.falling_2m;
            return (
              <Fragment key={e.id}>
                <tr
                  onClick={() => setOpenEmp(isOpen ? null : e.id)}
                  className={`cursor-pointer ${red ? "bg-rose-50 hover:bg-rose-100" : "hover:bg-slate-50"}`}
                  data-testid={`${testid}-row-${e.id}`}
                >
                  <td className="px-3 py-2 text-[10px] font-bold text-slate-400 font-mono-num">{i + 1}</td>
                  <td className={`px-3 py-2 font-semibold ${red ? "text-rose-700" : "text-slate-900"}`}>
                    <span className="inline-flex items-center gap-1.5">
                      {isOpen ? <ChevronDown className="h-3 w-3 text-slate-400" /> : <ChevronRight className="h-3 w-3 text-slate-400" />}
                      {e.name}
                      {e.falling_2m && (
                        <span className="text-[9px] uppercase tracking-wider bg-rose-700 text-white px-1.5 py-0.5 rounded-full font-bold whitespace-nowrap" data-testid={`${testid}-falling-${e.id}`}>
                          baja 2 meses seguidos
                        </span>
                      )}
                      {e.below_avg && (
                        <span className="text-[9px] uppercase tracking-wider bg-rose-600 text-white px-1.5 py-0.5 rounded-full font-bold whitespace-nowrap">
                          se esfuerza menos
                        </span>
                      )}
                    </span>
                  </td>
                  <td className={`px-3 py-2 text-right font-mono-num font-bold ${e.below_avg ? "text-rose-700" : "text-slate-900"}`}>
                    {fmtNum2(e.gen_pct)}%
                  </td>
                  <td className={`px-3 py-2 text-right font-mono-num font-bold ${e.delta_vs_team_pp > 0 ? "text-emerald-600" : e.below_avg ? "text-rose-700" : "text-slate-500"}`}>
                    {e.delta_vs_team_pp > 0 ? "+" : ""}{fmtNum2(e.delta_vs_team_pp)} pp
                  </td>
                  <td className="px-3 py-2 text-right font-mono-num text-slate-500">
                    {e.prev_gen_pct == null ? "—" : `${fmtNum2(e.prev_gen_pct)}%`}
                  </td>
                  <td className={`px-3 py-2 text-right font-mono-num font-bold ${(dm || 0) > 0 ? "text-emerald-600" : (dm || 0) < 0 ? "text-rose-600" : "text-slate-400"}`}>
                    {dm == null ? "—" : (
                      <span className="inline-flex items-center gap-0.5 justify-end">
                        {dm > 0 ? <TrendingUp className="h-3 w-3" /> : dm < 0 ? <TrendingDown className="h-3 w-3" /> : null}
                        {dm > 0 ? "+" : ""}{fmtNum2(dm)}
                      </span>
                    )}
                  </td>
                  <EvoCells values={e.evolution} months={evo} testid={`${testid}-evo-${e.id}`} />
                  <td className="px-3 py-2 text-right font-mono-num text-slate-600">
                    {e.units_generico.toLocaleString("es-ES")} / {e.units_marca.toLocaleString("es-ES")}
                  </td>
                  <td className="px-3 py-2 text-right font-mono-num text-rose-700">{fmtEur(e.eur_marca_perdida)}</td>
                  <td className="px-3 py-2 text-right font-mono-num text-slate-500">
                    {e.molecules_touched}/{d.totals.n_molecules}
                  </td>
                </tr>
                {isOpen && (
                  <tr className="bg-slate-50">
                    <td colSpan={9 + nEvo} className="px-3 py-3">
                      <EmployeeEvolutionChart months={evo} values={e.evolution} team={evo.map((m) => m.team_gen_pct)} name={e.name} testid={`${testid}-chart-${e.id}`} />
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1.5">
                        {e.name} · moléculas a incentivar (solo las que ha dispensado)
                      </div>
                      <div className="max-h-72 overflow-y-auto border border-slate-200 rounded bg-white">
                        <table className="w-full text-xs">
                          <thead className="bg-slate-100 text-[9px] uppercase tracking-wider text-slate-500 sticky top-0">
                            <tr>
                              <th className="text-left px-2 py-1">#</th>
                              <th className="text-left px-2 py-1">Molécula</th>
                              <th className="text-right px-2 py-1">Uds</th>
                              <th className="text-right px-2 py-1">Marca</th>
                              <th className="text-right px-2 py-1">Gen.</th>
                              <th className="text-right px-2 py-1">% gen</th>
                              <EvoHeaders months={evo} cls="px-2 py-1" light />
                              <th className="text-right px-2 py-1">€ marca</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-50">
                            {e.all_molecules.filter((m) => m.units_total > 0).map((m) => (
                              <tr key={m.gh} className={m.falling_2m ? "bg-rose-100 text-rose-800 font-semibold" : m.marca_units > 0 ? "bg-rose-50/60" : ""}>
                                <td className="px-2 py-1 font-mono-num text-slate-400">#{m.rank}</td>
                                <td className="px-2 py-1">{m.gh}</td>
                                <td className="px-2 py-1 text-right font-mono-num">{m.units_total}</td>
                                <td className="px-2 py-1 text-right font-mono-num text-rose-600">{m.marca_units}</td>
                                <td className="px-2 py-1 text-right font-mono-num text-emerald-700">{m.gen_units}</td>
                                <td className="px-2 py-1 text-right font-mono-num font-bold">{fmtNum2(m.gen_pct)}%</td>
                                <EvoCells values={m.evolution} months={evo} cls="px-2 py-1" />
                                <td className="px-2 py-1 text-right font-mono-num">{fmtEur(m.eur_marca)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
        <tfoot className="bg-slate-100 text-xs font-bold">
          <tr>
            <td className="px-3 py-2" colSpan={2}>MEDIA FARMACIA</td>
            <td className="px-3 py-2 text-right font-mono-num">{fmtNum2(d.totals.team_gen_pct)}%</td>
            <td className="px-3 py-2"></td>
            <td className="px-3 py-2 text-right font-mono-num text-slate-500">
              {d.totals.prev_team_gen_pct == null ? "—" : `${fmtNum2(d.totals.prev_team_gen_pct)}%`}
            </td>
            <td className={`px-3 py-2 text-right font-mono-num ${(d.totals.team_delta_pp || 0) > 0 ? "text-emerald-600" : (d.totals.team_delta_pp || 0) < 0 ? "text-rose-600" : "text-slate-400"}`}>
              {d.totals.team_delta_pp == null ? "—" : `${d.totals.team_delta_pp > 0 ? "+" : ""}${fmtNum2(d.totals.team_delta_pp)}`}
            </td>
            <EvoCells values={evo.map((m) => m.team_gen_pct)} months={evo} testid={`${testid}-evo-team`} />
            <td className="px-3 py-2 text-right font-mono-num">
              {d.totals.units_generico.toLocaleString("es-ES")} / {d.totals.units_marca.toLocaleString("es-ES")}
            </td>
            <td className="px-3 py-2 text-right font-mono-num text-rose-700">{fmtEur(d.totals.eur_marca_perdida)}</td>
            <td className="px-3 py-2 text-right font-mono-num text-slate-500">{d.totals.n_employees} pers.</td>
          </tr>
        </tfoot>
      </table>
    </div>
    </>
  );
}

/** Copia del resumen de incentivos para la pantalla de Análisis por empleado.
 *  No pinta nada si la farmacia no tiene el programa en marcha. */
export default function EmployeeIncentivesSummary({ dispId }) {
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let alive = true;
    setState({ loading: true, data: null, error: null });
    api
      .get("/employees/incentives-summary", { params: dispId ? { disp_id: dispId } : {} })
      .then((r) => alive && setState({ loading: false, data: r.data, error: null }))
      .catch((e) => alive && setState({ loading: false, data: null, error: e?.response?.data?.detail || "Error" }));
    return () => { alive = false; };
  }, [dispId]);

  if (state.loading) return null;
  const d = state.data;
  if (state.error || !d || !d.active || !d.employees?.length) return null;

  return (
    <div
      className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-6 shadow-sm"
      data-testid="emp-incentives-summary"
    >
      <h2 className="font-display text-base md:text-lg font-bold tracking-tight text-slate-950 mb-1">
        Moléculas que estás incentivando
      </h2>
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <Users className="h-4 w-4 text-indigo-600" />
        <span className="text-xs font-bold uppercase tracking-wider text-slate-700">
          Programa de incentivos en marcha · {d.month.label}
        </span>
        <span className="text-[11px] text-slate-500 font-mono-num">
          media farmacia {fmtNum2(d.totals.team_gen_pct)}%
          {d.totals.prev_team_gen_pct != null && ` · antes ${fmtNum2(d.totals.prev_team_gen_pct)}%`}
          {d.totals.n_below > 0 && <span className="text-rose-600 font-bold"> · {d.totals.n_below} por debajo</span>}
        </span>
      </div>
      <p className="text-[11px] text-slate-500 mb-2">
        % de genérico de cada empleado medido <strong>solo en las {d.totals.n_molecules} moléculas a incentivar</strong>.
        En <span className="text-rose-600 font-bold">rojo</span> quien está {fmtNum2(d.below_threshold_pp)} puntos o más
        por debajo de la media de la farmacia.
        {d.prev_month && <> Comparado con <strong>{d.prev_month.label}</strong>.</>}
      </p>
      <IncentivesEmployeeTable data={d} testid="emp-incentives" />
    </div>
  );
}
